import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ShieldCheck, UserPlus, UsersRound } from "lucide-react";

import api from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";

type TeamRole = "admin" | "director_comercial" | "comercial";

interface TeamMember {
  id: number;
  email: string;
  nombre: string | null;
  role: TeamRole;
  crmSupervisor: boolean;
  isActive: boolean;
  createdAt: string;
  lastLogin: string | null;
}

function errorMessage(error: unknown): string {
  if (axios.isAxiosError<{ error?: string }>(error)) {
    return error.response?.data?.error ?? "No se pudo completar la operación";
  }
  return "No se pudo completar la operación";
}

export function CrmEquipoTab() {
  const { user } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    nombre: "",
    email: "",
    password: "",
    role: "comercial" as TeamRole,
  });

  const loadMembers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get<{ data: TeamMember[] }>("/admin/crm/equipo");
      setMembers(response.data.data);
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  const createMember = async () => {
    setError("");
    setSuccess("");
    if (!form.nombre.trim() || !form.email.trim() || form.password.length < 8) {
      setError("Indica el nombre, un email válido y una contraseña de al menos 8 caracteres.");
      return;
    }

    setSaving(true);
    try {
      await api.post("/admin/crm/equipo", form);
      setForm({ nombre: "", email: "", password: "", role: "comercial" });
      setShowForm(false);
      setSuccess("Usuario comercial creado correctamente.");
      await loadMembers();
    } catch (saveError) {
      setError(errorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  const updateMember = async (
    member: TeamMember,
    updates: { role?: TeamRole; is_active?: boolean }
  ) => {
    setUpdatingId(member.id);
    setError("");
    setSuccess("");
    try {
      await api.patch(`/admin/crm/equipo/${member.id}`, updates);
      setSuccess("Permisos actualizados.");
      await loadMembers();
    } catch (updateError) {
      setError(errorMessage(updateError));
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Equipo comercial</h2>
          <p className="mt-1 text-sm text-gray-600">
            Crea accesos individuales y controla qué usuarios están activos.
          </p>
        </div>
        <Button onClick={() => setShowForm((current) => !current)}>
          <UserPlus className="h-4 w-4" />
          Nuevo usuario
        </Button>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <strong>Administración completa:</strong> accede a todo el panel con agenda personal. {" "}
        <strong>Director comercial:</strong> consulta toda la cartera CRM y todas las agendas. {" "}
        <strong>Comercial:</strong> consulta toda la cartera y puede interactuar con cualquier cliente;
        sus actividades, tareas, avisos y calendario quedan asociados a su propia cuenta.
      </div>

      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {success && <div className="rounded-lg bg-green-50 p-3 text-sm text-green-700">{success}</div>}

      {showForm && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Input
              label="Nombre"
              value={form.nombre}
              onChange={(event) => setForm((current) => ({ ...current, nombre: event.target.value }))}
            />
            <Input
              label="Email de acceso"
              type="email"
              value={form.email}
              onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
            />
            <Input
              label="Contraseña temporal"
              type="password"
              minLength={8}
              value={form.password}
              onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
            />
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Rol</span>
              <select
                value={form.role}
                onChange={(event) =>
                  setForm((current) => ({ ...current, role: event.target.value as TeamRole }))
                }
                className="min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              >
                <option value="comercial">Comercial</option>
                <option value="director_comercial">Director comercial</option>
                <option value="admin">Administración completa</option>
              </select>
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowForm(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={createMember} loading={saving}>
              Crear acceso
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center text-sm text-gray-500">Cargando equipo…</div>
      ) : members.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 py-12 text-center">
          <UsersRound className="mx-auto h-8 w-8 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">Todavía no hay usuarios comerciales.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {members.map((member) => (
            <div
              key={member.id}
              className="flex flex-col gap-3 rounded-xl border border-gray-200 p-4 lg:flex-row lg:items-center"
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div className="rounded-lg bg-blue-50 p-2 text-[#1A3AB8]">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="truncate font-medium text-gray-900">
                    {member.nombre || member.email}
                  </p>
                  {member.nombre && (
                    <p className="truncate text-xs text-gray-600">{member.email}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    Último acceso: {member.lastLogin ? new Date(member.lastLogin).toLocaleString("es-ES") : "nunca"}
                  </p>
                </div>
              </div>

              <Badge variant={member.isActive ? "success" : "neutral"}>
                {member.isActive ? "Activo" : "Inactivo"}
              </Badge>

              <select
                value={member.role}
                disabled={
                  updatingId === member.id ||
                  (member.crmSupervisor && !user?.crmSupervisor)
                }
                onChange={(event) =>
                  void updateMember(member, { role: event.target.value as TeamRole })
                }
                className="min-h-10 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                aria-label={`Rol de ${member.email}`}
              >
                <option value="comercial">Comercial</option>
                <option value="director_comercial">Director comercial</option>
                <option value="admin">Administración completa</option>
              </select>

              <Button
                variant="secondary"
                size="sm"
                loading={updatingId === member.id}
                disabled={member.crmSupervisor && !user?.crmSupervisor}
                onClick={() => void updateMember(member, { is_active: !member.isActive })}
              >
                {member.isActive ? "Desactivar" : "Activar"}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
